/**
 * WhatsNewModal — the pop-up that shows recent release notes.
 *
 * Used two ways:
 *   • The gift bell opens it on demand.
 *   • WhatsNewAutoPopup opens it automatically the first time a user opens the
 *     app after a new release ("automatically pushes the changelog").
 *
 * Theme-aware overlay. z-[200] sits above the nav (z-50) and mobile drawer
 * (z-[101]) but below emergency SOS overlays (z-[250]) so a field alert always
 * wins.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Gift, X, ArrowRight } from 'lucide-react';
import { WhatsNewFeed } from './WhatsNewFeed';
import { WHATS_NEW_RELEASES, LATEST_WHATS_NEW_ID } from '@/data/whatsNew';

/** The popup shows only the most recent releases so it doesn't grow into an
 *  ever-longer wall over time; the full history lives on the What's New page. */
const MAX_POPUP_RELEASES = 8;

interface WhatsNewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional — when provided, shows a "See full history" link (office only). */
  onViewAll?: () => void;
}

export function WhatsNewModal({ isOpen, onClose, onViewAll }: WhatsNewModalProps) {
  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  // Portal to <body> so the overlay escapes the nav's backdrop-filter, which
  // otherwise becomes the containing block for `position: fixed` and pins the
  // modal to the nav bar instead of the viewport.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="What's new"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-surface-overlay backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        className="relative w-full sm:max-w-lg max-h-[88vh] sm:max-h-[85vh] flex flex-col bg-surface-card border border-theme rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="relative shrink-0 px-5 py-4 border-b border-theme-soft bg-gradient-to-br from-brand-secondary/10 to-transparent">
          <div className="flex items-center gap-3 pr-8">
            <div className="w-9 h-9 rounded-xl bg-brand-secondary/15 border border-brand-secondary/25 flex items-center justify-center shrink-0">
              <Gift className="w-5 h-5 text-brand-secondary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-theme-primary leading-tight">What's new</h2>
              <p className="text-xs text-theme-muted">The latest updates and fixes, in plain English.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3.5 right-3.5 w-8 h-8 flex items-center justify-center rounded-lg text-theme-tertiary hover:text-theme-primary hover:bg-surface-hover transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <WhatsNewFeed
            releases={WHATS_NEW_RELEASES.slice(0, MAX_POPUP_RELEASES)}
            latestId={LATEST_WHATS_NEW_ID}
            compact
          />
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-3 border-t border-theme-soft bg-surface-card">
          {onViewAll ? (
            <button
              onClick={() => {
                onViewAll();
                onClose();
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-secondary hover:underline"
            >
              See full history
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-brand-secondary/15 text-brand-secondary border border-brand-secondary/25 hover:bg-brand-secondary/25 transition-all"
          >
            Got it
          </button>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}
